// @ts-strict-ignore
import {
    CreateIntegrationRequest,
    CreateIntegrationRequestDataType,
    tryCreateIntegrationRequest,
} from '@/actions/integrations';
import { StepConfigUpdatable, UpdateStepConfigRequest } from '@/actions/stepConfig';
import { trackStepConfigEvent } from '@/actions/trackEvent';
import IntegrationsForCapabilityComponent from '@/components/IntegrationsForCapabilityComponent';
import {
    defaultWarningTextField,
    getStepConfigSettingsInitialValuesByArchetype,
    isStepSettingFieldShownInDrawer,
    RELEASE_LEVEL_APP_STORE_SETTING_KEYS,
} from '@/components/stepConfigs/stepConfigSettingsForms';
import { FetchableEntityTypeV2 } from '@/entityType';
import {
    allSupportedIntegrationTypesForCapability,
    displayNameForCapability,
    getError,
    getIsLoading,
    getUpdated,
} from '@/helpers';
import { FieldType, renderFormFields } from '@/helpers/formHelpers';
import { formFieldsFromSettingFields, SettingFormFieldResolver } from '@/helpers/settingFieldHelpers';
import { INTEGRATIONS_SETTINGS_GROUP, useStepConfigSettingFields } from '@/utils/hooks/useStepConfigSettingFields';
import { BranchConfigPreview, FeatureAffiliationConfigPreview, TagConfigPreview } from './SharedConfigFields';
import { integrationSelectOptions } from '@/helpers/integrationSelectHelpers';
import {
    authTypeForIntegrationType,
    capabilitySupportsMultipleInstallationsPerStep,
    displayNameForIntegrationType,
    integrationUpdatableForIntegrationType,
} from '@/helpers/integrationHelpers';
import { RootState } from '@/reducers/reducers';
import {
    selectAppIntegrations,
    selectBranchConfigsById,
    selectConnectedIntegrationsForStepConfig,
    selectFeatureAffiliationConfigsById,
    selectIntegrationFromIntegrationType,
    selectIsAppsDemoModeEnabled,
    selectIsRunwayOrg,
    selectManageableWorkflowSteps,
    selectStepConfig,
    selectTagConfigsById,
} from '@/selectors/selectors';
import { ReleaseStepSettings, ReleaseStepSettingsRequestValues, StepConfig } from '@/types/stepConfig';
import { AgeRatingSettingsField } from './ageRating/AgeRatingSettingsField';
import { RetainedBuildsSettingsField } from './RetainedBuildsSettingsField';
import { AndroidStagedRolloutPercentageInput } from '../sharedComponents/shared';
import * as AnalyticEvents from '@/utils/analytics';
import { globalMessage } from '@/utils/globalMessage';
import { useCallUpdatable, useGetEntityV2 } from '@/utils/hooks/helpers';
import { ApiOutlined, CopyOutlined, SettingOutlined } from '@ant-design/icons';
import Badge from 'antd/lib/badge';
import Alert from 'antd/lib/alert/Alert';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Divider from 'antd/lib/divider';
import Drawer from 'antd/lib/drawer';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Menu from 'antd/lib/menu';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Spin from 'antd/lib/spin';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import * as React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
    AllIntegrationCapabilities,
    AllIntegrationTypes,
    AppleAppStoreDefaultSettings,
    Integration,
    IntegrationCapability,
    IntegrationErrorType,
    IntegrationType,
    ReleaseStepType,
    RunwayResponseType,
    SettingFieldType,
} from '../../types';
import IntegrationConfiguration from '../IntegrationConfiguration';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import { useOauthIntegrationInstaller } from '../setup/hooks';
import { useUpdateIntegrationModal } from '../setup/useUpdateIntegrationModal';
import { antGrey, runwayColors, titleStyle } from '../styles/globalStyles';
import PermissionedActionButton from '@/components/HOCs/PermissionedActionButton';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { Destination } from '@/types/destinations';
import { DestinationCounter } from '../appHome/DestinationCounter';
import { ExtendedForm, ExtendedFormObject, ExtendedFormState } from '@/utils/ExtendedForm';
import { isEqual, isUndefined, omitBy, pick, set } from 'lodash';
import UpdateReleaseSettingsScopeModal, { UpdateReleaseSettingsScope } from './UpdateReleaseSettingsScopeModal';
import { MenuItemType } from 'antd/lib/menu/interface';
import { useExtendedForm } from '@/utils/hooks/useExtendedForm';
import Flex from 'antd/lib/flex';
import { DeleteStepLink } from '../appSettings/DeleteStepLink';

export enum MenuKey {
    Settings = 'Settings',
    Integrations = 'Integrations',
    Schedule = 'Schedule',
    Automations = 'Automations',
    Checklists = 'Checklists',
}

const getButtonTextForMenuKey = (menuKey: MenuKey): string => {
    switch (menuKey) {
        case MenuKey.Settings:
            return 'Save settings';
        case MenuKey.Integrations:
            return 'Save integrations';
        case MenuKey.Schedule:
            return 'Save schedule';
        case MenuKey.Automations:
            return 'Save automations';
        case MenuKey.Checklists:
            return 'Save checklists';
        default:
            return 'Save settings';
    }
};

const getMenuItemBadgeState = (
    menuKey: MenuKey,
    formStatesById: Partial<Record<MenuKey, ExtendedFormState[]>>,
): { showBadge: boolean; color: 'error' | 'warning'; tooltipText: string } | null => {
    const formsForMenuKey = formStatesById[menuKey];

    const hasInvalidForms = formsForMenuKey.some((form) => form.isInvalid);
    const hasDirtyForms = formsForMenuKey.some((form) => form.isDirty);

    if (hasInvalidForms) {
        return {
            showBadge: true,
            color: 'error',
            tooltipText: 'This tab has validation errors that need to be addressed.',
        };
    } else if (hasDirtyForms) {
        return {
            showBadge: true,
            color: 'warning',
            tooltipText: 'This tab has unsaved changes.',
        };
    }

    return null;
};

const createMenuItemWithBadge = (
    currentMenuKey: MenuKey,
    menuKey: MenuKey,
    icon: React.ReactNode,
    formStatesById: Partial<Record<MenuKey, ExtendedFormState[]>>,
): MenuItemType => {
    const isSelected = currentMenuKey === menuKey;
    const badgeState = getMenuItemBadgeState(menuKey, formStatesById);

    const labelContent = (
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space>
                {icon}

                <Text style={{ color: isSelected ? runwayColors.primary : antGrey.grey9 }}>{menuKey}</Text>
            </Space>
            {badgeState?.showBadge && (
                <Tooltip title={badgeState.tooltipText}>
                    <Badge status={badgeState.color} />
                </Tooltip>
            )}
        </Space>
    );

    return {
        label: labelContent,
        key: menuKey,
    };
};

export interface StepRunSettingsDrawerProps {
    appId: string;
    orgId: string;
    stepConfigId: string;
    workflowId: string;
    originStepRunId?: string;
    isOriginReleaseActive?: boolean;
    releaseStepTypeText: string;
    isInsideStep?: boolean;
    onStepDeleted?: () => void;
    isOpen: boolean;
    initialMenuKey?: MenuKey;
    stepName?: string;
    onClose?: () => void;
}

type StepConfigGeneralSettingsFormType = Pick<StepConfig, 'name'>;

const releaseLevelAppStoreSettings = (appStoreSettings: unknown): Record<string, unknown> =>
    omitBy(pick(appStoreSettings ?? {}, RELEASE_LEVEL_APP_STORE_SETTING_KEYS), isUndefined);

const withSkipCustomisedReleases = (
    request: UpdateStepConfigRequest,
    skipReleasesWithCustomAppStoreSettings: boolean,
): UpdateStepConfigRequest => ({
    ...request,
    stepConfigArchetypeSpecificSettings: {
        ...request.stepConfigArchetypeSpecificSettings,
        skipReleasesWithCustomAppStoreSettings,
    } as ReleaseStepSettingsRequestValues,
});

export const StepConfigSettingsDrawer: React.FC<StepRunSettingsDrawerProps> = ({
    appId,
    orgId,
    stepConfigId,
    workflowId,
    originStepRunId,
    isOriginReleaseActive = false,
    releaseStepTypeText,
    stepName,
    isOpen,
    initialMenuKey,
    isInsideStep,
    onStepDeleted,
    onClose,
}) => {
    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const [getStepConfig, getStepConfigIsLoading, getStepConfigError] = useGetEntityV2(
        FetchableEntityTypeV2.StepConfig,
        stepConfigId,
    );

    const stepConfig = useSelector((state: RootState) => selectStepConfig(state, stepConfigId));

    const dispatch = useDispatch();

    // only track the view once per open, and only once the step config is loaded so its properties are available
    const didTrackViewRef = React.useRef(false);
    React.useEffect(() => {
        if (!isOpen) {
            didTrackViewRef.current = false;
            return;
        }

        if (didTrackViewRef.current || !stepConfig) {
            return;
        }

        didTrackViewRef.current = true;
        dispatch(
            trackStepConfigEvent({
                orgId,
                appId,
                stepConfigId,
                eventName: AnalyticEvents.Event.ViewStepConfig,
            }),
        );
    }, [isOpen, stepConfig, orgId, appId, stepConfigId, dispatch]);

    React.useEffect(() => {
        if (getStepConfigError) {
            globalMessage.error('Failed to load step settings');
        }
    }, [getStepConfigError]);

    const isStepConfigLoaded = !!stepConfig;
    React.useEffect(() => {
        if (isOpen && !isViewingAppsInDemoMode && !isStepConfigLoaded) {
            getStepConfig({
                workflowId,
                stepConfigId,
                orgId,
            });
        }
    }, [isOpen, isViewingAppsInDemoMode, isStepConfigLoaded, stepConfigId, workflowId, orgId]);

    // to intercept oauth installation redirects
    useOauthIntegrationInstaller({});

    const [activeMenuKey, setActiveMenuKey] = React.useState<MenuKey>(MenuKey.Settings);
    React.useEffect(() => {
        if (isOpen && initialMenuKey) {
            setActiveMenuKey(initialMenuKey);
        }
    }, [isOpen, initialMenuKey]);

    const history = useHistory();
    const lastInstalledIntegrationType = history.location.state?.['lastInstalledIntegrationType'];
    const lastInstalledIntegration = useSelector((state: RootState) =>
        selectIntegrationFromIntegrationType(state, appId, lastInstalledIntegrationType),
    );

    React.useEffect(() => {
        if (lastInstalledIntegration) {
            setSelectedIntegration(lastInstalledIntegration);
        }
    }, [lastInstalledIntegration]);

    const allAppIntegrations = useSelector((state: RootState) => selectAppIntegrations(state, appId));

    const [selectedIntegration, setSelectedIntegration] = React.useState<Integration | null>(null);

    //handles configuring existing integration
    const { setShowUpdateIntegrationModal, updateIntegrationModalProps } = useUpdateIntegrationModal({
        orgId,
        appId,
        selectedIntegration,
        closable: false,
    });

    const addIntegrationFailed = useSelector((state: RootState) => {
        const errorState = {};
        AllIntegrationTypes.forEach((integrationType) => {
            const authType = authTypeForIntegrationType(integrationType);
            if (authType) {
                errorState[integrationType] = getError(authType, state);
            }
        });

        allAppIntegrations.forEach((integration) => {
            const authType = authTypeForIntegrationType(integration.id);
            if (authType) {
                errorState[integration.installationId] = getError(authType, state, integration.installationId);
            }
        });

        return errorState;
    }, shallowEqual);

    const updateIntegrationSucceeded = useSelector((state: RootState) => {
        const successState = {};
        allAppIntegrations.forEach((integration) => {
            const updateType = integrationUpdatableForIntegrationType(integration.id);
            if (updateType) {
                successState[integration.installationId] = getUpdated(updateType, state, integration.installationId);
            }
        });

        return successState;
    }, shallowEqual);

    const updateIntegrationFailed = useSelector((state: RootState) => {
        const errorState = {};
        allAppIntegrations.forEach((integration) => {
            const updateType = integrationUpdatableForIntegrationType(integration.id);
            if (updateType) {
                errorState[integration.installationId] = getError(updateType, state, integration.installationId);
            }
        });
        return errorState;
    }, shallowEqual);

    // Add integration loading and success state
    const addIntegrationIsLoading = useSelector((state: RootState) => {
        const loadingState = {};
        AllIntegrationTypes.forEach((integrationType) => {
            const authType = authTypeForIntegrationType(integrationType);
            if (authType) {
                loadingState[integrationType] = getIsLoading(authType, state);
            }
        });
        return loadingState;
    }, shallowEqual);

    const addIntegrationSucceeded = useSelector((state: RootState) => {
        const successState = {};
        AllIntegrationTypes.forEach((integrationType) => {
            const authType = authTypeForIntegrationType(integrationType);
            if (authType) {
                successState[integrationType] = getUpdated(authType, state);
            }
        });

        allAppIntegrations.forEach((integration) => {
            const authType = authTypeForIntegrationType(integration.id);
            if (authType) {
                const updated = getUpdated(authType, state, integration.installationId);
                successState[integration.installationId] = updated;
            }
        });

        return successState;
    }, shallowEqual);

    // Handle integration status changes
    React.useEffect(() => {
        // New integration errors
        Object.entries(addIntegrationFailed).forEach(([_, errorMessage]) => {
            if (errorMessage) {
                globalMessage.error(String(errorMessage));
            }
        });

        // New integration success
        Object.entries(addIntegrationSucceeded).forEach(([integrationId, updated]) => {
            if (updated) {
                const integration = allAppIntegrations.find(
                    (integration) => integration.installationId === integrationId,
                );

                if (integration && !integration.integrationError) {
                    const integrationDisplayName = displayNameForIntegrationType(integration.id);
                    globalMessage.success(`${integrationDisplayName} integration added!`);
                } else if (
                    integration &&
                    integration.integrationError?.status === IntegrationErrorType.IncompleteData
                ) {
                    setSelectedIntegration(integration);
                    setShowUpdateIntegrationModal(true);
                } else if (integration && integration.integrationError) {
                    globalMessage.error(integration.integrationError.message);
                } else {
                    globalMessage.error('Something went wrong, please try again');
                }
            }
        });

        // Update integration failure
        Object.entries(updateIntegrationFailed).forEach(([_, errorMessage]) => {
            if (errorMessage) {
                globalMessage.error(String(errorMessage));
            }
        });

        // Update integration success
        Object.entries(updateIntegrationSucceeded).forEach(([_, updated]) => {
            if (updated) {
                setSelectedIntegration(null);
                setShowUpdateIntegrationModal(false);
            }
        });
    }, [addIntegrationFailed, addIntegrationSucceeded, updateIntegrationFailed, updateIntegrationSucceeded]);

    const [updateStepConfig, updateStepConfigIsLoading, updateStepConfigResponseType, updateStepConfigError] =
        useCallUpdatable(StepConfigUpdatable.UpdateStepConfig, {
            workflowId,
            orgId,
            stepConfigId,
        });

    const destinations = stepConfig?.destinations;

    const [requestPendingScopeChoice, setRequestPendingScopeChoice] = React.useState<UpdateStepConfigRequest | null>(
        null,
    );
    const isReleaseStep =
        stepConfig?.archetype === ReleaseStepType.Submission ||
        stepConfig?.archetype === ReleaseStepType.StoreReview ||
        stepConfig?.archetype === ReleaseStepType.Takeoff;
    const releasesWithCustomAppStoreSettings =
        isReleaseStep ?
            ((stepConfig.settings as ReleaseStepSettings | undefined)?.releasesWithCustomAppStoreSettings ?? [])
        :   [];

    const [stepConfigGeneralSettingsExtendedFormState, stepConfigGeneralSettingsExtendedForm] =
        useExtendedForm<StepConfigGeneralSettingsFormType>();
    const [stepConfigArchetypeSpecificSettingsExtendedFormState, stepConfigArchetypeSpecificSettingsExtendedForm] =
        useExtendedForm<StepConfigArchetypeSpecificSettingsFormType>();
    const [integrationsExtendedFormState, integrationsExtendedForm] = useExtendedForm<IntegrationsFormValues>();
    const didHandleSaveSuccessRef = React.useRef(false);

    React.useEffect(() => {
        const didSucceed = updateStepConfigResponseType === RunwayResponseType.Success;
        if (didSucceed && !didHandleSaveSuccessRef.current) {
            didHandleSaveSuccessRef.current = true;
            setRequestPendingScopeChoice(null);
            globalMessage.success('Step settings updated successfully');
            dispatch(
                trackStepConfigEvent({
                    orgId,
                    appId,
                    stepConfigId,
                    eventName: AnalyticEvents.Event.UpdateStepConfig,
                }),
            );
        }

        if (!didSucceed) {
            didHandleSaveSuccessRef.current = false;
        }
    }, [updateStepConfigResponseType, orgId, appId, stepConfigId, dispatch]);

    React.useEffect(() => {
        if (updateStepConfigError) {
            globalMessage.error(updateStepConfigError);
        }
    }, [updateStepConfigError]);

    const formStatesById = {
        [MenuKey.Settings]: [
            stepConfigGeneralSettingsExtendedFormState,
            stepConfigArchetypeSpecificSettingsExtendedFormState,
        ],
        [MenuKey.Integrations]: [integrationsExtendedFormState],
    };

    const areAllFormsPristine = Object.values(formStatesById).every((formStates) =>
        formStates.every((formState) => !formState),
    );
    const isAnyFormInvalid = Object.values(formStatesById).some((formStates) =>
        formStates.some((formState) => formState.isInvalid),
    );

    const isCurrentFormInvalid = formStatesById[activeMenuKey]?.some((formState) => formState.isInvalid) ?? false;
    const isCurrentFormDirty = formStatesById[activeMenuKey]?.some((formState) => formState.isDirty) ?? false;

    const isCurrentFormPristine = !isCurrentFormDirty;

    const manageableWorkflowSteps = useSelector((state: RootState) => selectManageableWorkflowSteps(state, appId));
    const canDeleteStep = manageableWorkflowSteps.some((option) => option.archetype === stepConfig?.archetype);

    return (
        <>
            <UpdateReleaseSettingsScopeModal
                open={requestPendingScopeChoice !== null}
                releases={releasesWithCustomAppStoreSettings}
                isLoading={updateStepConfigIsLoading}
                onConfirm={(scope) => {
                    if (!requestPendingScopeChoice) {
                        return;
                    }
                    updateStepConfig(
                        withSkipCustomisedReleases(
                            requestPendingScopeChoice,
                            scope === UpdateReleaseSettingsScope.ExcludeCustomisedReleases,
                        ),
                    );
                }}
                onCancel={() => setRequestPendingScopeChoice(null)}
            />
            {selectedIntegration && (
                <IntegrationConfiguration
                    {...updateIntegrationModalProps}
                    onClose={() => {
                        setShowUpdateIntegrationModal(false);
                        setSelectedIntegration(null);
                    }}
                />
            )}
            <Drawer
                destroyOnClose
                title={
                    <TopBar
                        releaseStepTypeText={releaseStepTypeText}
                        stepName={stepName}
                        isCurrentFormInvalid={isCurrentFormInvalid}
                        isCurrentFormPristine={isCurrentFormPristine}
                        isViewingAppsInDemoMode={isViewingAppsInDemoMode}
                        areAllFormsPristine={areAllFormsPristine}
                        isAnyFormInvalid={isAnyFormInvalid}
                        updateStepConfigIsLoading={updateStepConfigIsLoading}
                        activeMenuKey={activeMenuKey}
                        destinations={destinations}
                        onSaveClick={async (saveAll: boolean) => {
                            try {
                                const requestData: UpdateStepConfigRequest = {};

                                if (originStepRunId) {
                                    requestData.originStepRunId = originStepRunId;
                                }

                                const menuKeysToSave =
                                    saveAll ? [MenuKey.Settings, MenuKey.Integrations] : [activeMenuKey];
                                let appStoreSettingsChanged = false;

                                for (const menuKey of menuKeysToSave) {
                                    switch (menuKey) {
                                        case MenuKey.Settings: {
                                            const stepConfigGeneralSettingsFormValues =
                                                await stepConfigGeneralSettingsExtendedForm.form.validateFields();
                                            const stepConfigArchetypeSpecificSettingsFormValues =
                                                await stepConfigArchetypeSpecificSettingsExtendedForm.form.validateFields();

                                            // Google Play and Huawei Review and Release steps don't offer staged release, so their form saves no app store settings
                                            if (
                                                isReleaseStep &&
                                                'appStoreSettings' in stepConfigArchetypeSpecificSettingsFormValues
                                            ) {
                                                const initialValues =
                                                    getStepConfigSettingsInitialValuesByArchetype(stepConfig);
                                                appStoreSettingsChanged = !isEqual(
                                                    releaseLevelAppStoreSettings(
                                                        stepConfigArchetypeSpecificSettingsFormValues.appStoreSettings,
                                                    ),
                                                    releaseLevelAppStoreSettings(
                                                        'appStoreSettings' in initialValues ?
                                                            initialValues.appStoreSettings
                                                        :   undefined,
                                                    ),
                                                );
                                            }

                                            requestData.stepConfigGeneralSettings = {
                                                name: stepConfigGeneralSettingsFormValues.name,
                                            };
                                            requestData.stepConfigArchetypeSpecificSettings =
                                                stepConfigArchetypeSpecificSettingsFormValues as unknown as typeof requestData.stepConfigArchetypeSpecificSettings;
                                            break;
                                        }
                                        case MenuKey.Integrations: {
                                            const integrationsFormValues =
                                                await integrationsExtendedForm.form.validateFields();
                                            const { integrationInstallationIdByCapability, ...integrationSettings } =
                                                integrationsFormValues;
                                            if (Object.keys(integrationSettings).length > 0) {
                                                requestData.stepConfigArchetypeSpecificSettings = {
                                                    ...requestData.stepConfigArchetypeSpecificSettings,
                                                    ...integrationSettings,
                                                    integrationInstallationIdByCapability,
                                                } as unknown as typeof requestData.stepConfigArchetypeSpecificSettings;
                                            }
                                            const requestedIds = Object.values<string | string[]>(
                                                integrationInstallationIdByCapability,
                                            ).reduce<string[]>((acc, value) => {
                                                if (Array.isArray(value)) {
                                                    acc.push(...value.filter((id) => id));
                                                } else if (value) {
                                                    acc.push(value);
                                                }
                                                return acc;
                                            }, []);
                                            requestData.integrationInstallationIds = Array.from(new Set(requestedIds));
                                            break;
                                        }
                                        default:
                                            break;
                                    }
                                }

                                if (appStoreSettingsChanged && releasesWithCustomAppStoreSettings.length > 0) {
                                    setRequestPendingScopeChoice(requestData);
                                    return;
                                }

                                updateStepConfig(requestData);
                            } catch (error) {
                                console.error('error saving step config', error);
                                globalMessage.error('Please correct all errors before saving');
                            }
                        }}
                    />
                }
                closeIcon={false}
                width={1024}
                open={isOpen}
                onClose={onClose}
                styles={{
                    header: {
                        padding: 0,
                    },
                    body: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                    },
                }}
            >
                {getStepConfigIsLoading ?
                    <Spin
                        size="large"
                        spinning={getStepConfigIsLoading}
                        style={{
                            alignSelf: 'center',
                        }}
                    />
                :   <>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                paddingTop: 24,
                                alignSelf: 'stretch',
                                flex: 1,
                                maxWidth: '100%',
                            }}
                        >
                            <Flex
                                vertical
                                style={{
                                    width: 208,
                                    flexShrink: 0,
                                    borderRight: `1px solid ${antGrey.grey4}`,
                                }}
                            >
                                <Menu
                                    style={{ flex: 1, borderInlineEnd: 'none' }}
                                    onClick={(menuInfo) => {
                                        setActiveMenuKey(menuInfo.key as MenuKey);
                                    }}
                                    selectedKeys={[activeMenuKey]}
                                    mode="vertical"
                                    disabled={updateStepConfigIsLoading}
                                    items={[
                                        createMenuItemWithBadge(
                                            activeMenuKey,
                                            MenuKey.Settings,
                                            <SettingOutlined />,
                                            formStatesById,
                                        ),
                                        createMenuItemWithBadge(
                                            activeMenuKey,
                                            MenuKey.Integrations,
                                            <ApiOutlined />,
                                            formStatesById,
                                        ),
                                    ]}
                                />
                                {canDeleteStep && (
                                    <div style={{ padding: 16 }}>
                                        <DeleteStepLink
                                            orgId={orgId}
                                            workflowId={workflowId}
                                            stepConfigId={stepConfigId}
                                            stepName={stepConfig.name}
                                            onDeleted={onStepDeleted ?? onClose}
                                        />
                                    </div>
                                )}
                            </Flex>
                            {stepConfig && (
                                <div
                                    style={{
                                        flex: 1,
                                        paddingRight: 24,
                                        paddingLeft: 24,
                                        overflowY: 'auto',
                                    }}
                                >
                                    {isInsideStep && (
                                        <Alert
                                            showIcon
                                            type="warning"
                                            message={
                                                isOriginReleaseActive ?
                                                    'Settings saved here will apply to all active releases (including this one) and all future releases.'
                                                :   "Settings saved here will apply to any active and future releases. The current release is no longer active and won't be updated."
                                            }
                                            style={{ marginBottom: 24 }}
                                        />
                                    )}
                                    <Settings
                                        hidden={activeMenuKey !== MenuKey.Settings}
                                        orgId={orgId}
                                        workflowId={workflowId}
                                        appId={appId}
                                        stepConfig={stepConfig}
                                        isInsideStep={isInsideStep}
                                        isDisabled={updateStepConfigIsLoading}
                                        stepConfigGeneralSetting={stepConfigGeneralSettingsExtendedForm}
                                        extendedFormObjectForArchetypeSpecificSettings={
                                            stepConfigArchetypeSpecificSettingsExtendedForm
                                        }
                                    />

                                    <Integrations
                                        hidden={activeMenuKey !== MenuKey.Integrations}
                                        orgId={orgId}
                                        appId={appId}
                                        workflowId={workflowId}
                                        stepConfig={stepConfig}
                                        addIntegrationIsLoading={addIntegrationIsLoading}
                                        addIntegrationSucceeded={addIntegrationSucceeded}
                                        isDisabled={updateStepConfigIsLoading}
                                        extendedFormObject={integrationsExtendedForm}
                                    />
                                </div>
                            )}
                        </div>
                    </>
                }
            </Drawer>
        </>
    );
};

type StepConfigArchetypeSpecificSettingsFormType = Record<string, unknown>;

const INITIAL_STAGED_ROLLOUT_PERCENTAGE_KEY = 'appStoreSettings.initialStagedRolloutPercentage';

const resolveStepConfigSettingFormField =
    (appId: string, stepConfig: StepConfig, isEditable: boolean): SettingFormFieldResolver =>
    (settingField, formField) => {
        const destination = stepConfig.destinations[0];
        const releaseSettings = stepConfig.settings as ReleaseStepSettings | undefined;
        switch (settingField.type) {
            case SettingFieldType.AgeRating: {
                const appStoreSettings = releaseSettings?.appStoreSettings as AppleAppStoreDefaultSettings | undefined;
                return {
                    fieldType: FieldType.Custom,
                    skipFormItemWrapper: true,
                    renderField: () => (
                        <AgeRatingSettingsField
                            appId={appId}
                            stepConfigId={stepConfig.id}
                            declaration={appStoreSettings?.ageRatingDeclaration}
                            ascDeclaration={releaseSettings?.ageRatingDeclaration}
                            storedQuestionSetVersion={appStoreSettings?.ageRatingQuestionSetVersion}
                            appDefaultDeclaration={releaseSettings?.ageRatingAppDefault}
                            questionSet={releaseSettings?.ageRatingQuestionSet}
                            isEditable={isEditable}
                        />
                    ),
                };
            }
            case SettingFieldType.RetainedBuilds:
                return {
                    ...formField,
                    label: '',
                    tooltip: undefined,
                    helpMessage: undefined,
                    renderField: () => (
                        <RetainedBuildsSettingsField
                            appId={appId}
                            stepConfigId={stepConfig.id}
                            platform={destination.platform}
                            appStoreType={destination.appStoreType}
                            isEditable={isEditable}
                        />
                    ),
                };
            case SettingFieldType.Number:
                return settingField.key === INITIAL_STAGED_ROLLOUT_PERCENTAGE_KEY ?
                        {
                            ...formField,
                            renderField: () => (
                                <AndroidStagedRolloutPercentageInput
                                    style={{ width: '100%', maxWidth: 200 }}
                                    placeholder="Enter percentage"
                                    min={settingField.min}
                                    max={settingField.max}
                                />
                            ),
                        }
                    :   undefined;
            default:
                return undefined;
        }
    };

const TopBar: React.FC<{
    releaseStepTypeText: string;
    stepName?: string;
    isCurrentFormInvalid: boolean;
    isCurrentFormPristine: boolean;
    isViewingAppsInDemoMode: boolean;
    areAllFormsPristine: boolean;
    isAnyFormInvalid: boolean;
    updateStepConfigIsLoading: boolean;
    activeMenuKey: MenuKey;
    destinations: Destination[];
    onSaveClick: (saveAll: boolean) => void;
}> = ({
    stepName,
    isCurrentFormInvalid,
    isCurrentFormPristine,
    isViewingAppsInDemoMode,
    areAllFormsPristine,
    isAnyFormInvalid,
    updateStepConfigIsLoading,
    activeMenuKey,
    onSaveClick,
    destinations,
}) => {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: 76,
                width: '100%',
                paddingRight: 24,
                paddingLeft: 36,
                flexGrow: 0,
                flexShrink: 0,
                backgroundColor: antGrey.grey3,
            }}
        >
            <Space
                split={
                    <Divider
                        type="vertical"
                        style={{
                            backgroundColor: antGrey.grey5,
                            height: 22,
                        }}
                    />
                }
            >
                <Text
                    style={{
                        ...titleStyle,
                        fontSize: 18,
                    }}
                >
                    {stepName}
                </Text>
                {destinations && <DestinationCounter destinations={destinations} />}
            </Space>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAUpdateAppSettings}
                    type="primary"
                    disabled={
                        isCurrentFormPristine ||
                        isCurrentFormInvalid ||
                        updateStepConfigIsLoading ||
                        isViewingAppsInDemoMode
                    }
                    loading={updateStepConfigIsLoading}
                    onClick={() => onSaveClick(false)}
                    menu={{
                        items: [
                            {
                                disabled: areAllFormsPristine || isAnyFormInvalid || updateStepConfigIsLoading,
                                key: 'save',
                                label: 'Save everything',
                                onClick: () => onSaveClick(true),
                            },
                        ],
                    }}
                >
                    {getButtonTextForMenuKey(activeMenuKey)}
                </PermissionedActionButton>
            </div>
        </div>
    );
};

const Settings: React.FC<{
    orgId: string;
    workflowId: string;
    appId: string;
    stepConfig: StepConfig;
    hidden: boolean;
    isInsideStep?: boolean;
    isDisabled?: boolean;
    stepConfigGeneralSetting: ExtendedFormObject<StepConfigGeneralSettingsFormType>;
    extendedFormObjectForArchetypeSpecificSettings: ExtendedFormObject<StepConfigArchetypeSpecificSettingsFormType>;
}> = ({
    orgId,
    workflowId,
    stepConfig,
    appId,
    hidden,
    isInsideStep,
    isDisabled,
    stepConfigGeneralSetting,
    extendedFormObjectForArchetypeSpecificSettings,
}) => {
    const branchConfigs = useSelector((state: RootState) => selectBranchConfigsById(state, appId));
    const tagConfigs = useSelector((state: RootState) => selectTagConfigsById(state, appId));
    const featureAffiliationConfigs = useSelector((state: RootState) =>
        selectFeatureAffiliationConfigsById(state, appId),
    );

    const branchConfig = branchConfigs[stepConfig.branchConfigId];
    const tagConfig = tagConfigs[stepConfig.tagConfigId];
    const featureAffiliationConfig = featureAffiliationConfigs[stepConfig.featureAffiliationConfigId];

    const { settingFields, isSettingFieldsError } = useStepConfigSettingFields({
        orgId,
        workflowId,
        stepConfigId: stepConfig.id,
    });
    const stepSettingFields = (settingFields ?? []).filter(
        (field) =>
            field.group !== INTEGRATIONS_SETTINGS_GROUP && isStepSettingFieldShownInDrawer(stepConfig.archetype, field),
    );

    const stepFormFields = formFieldsFromSettingFields(
        stepSettingFields,
        undefined,
        resolveStepConfigSettingFormField(appId, stepConfig, !isDisabled),
    );
    const stepConfigSettingsFields =
        stepFormFields.length > 0 && !isInsideStep ? [...stepFormFields, defaultWarningTextField] : stepFormFields;
    // The dirty check compares against these, so clearing a saved text setting counts as a change
    const stepConfigSettingsInitialValues = stepFormFields.reduce(
        (values, field) =>
            field.name !== undefined && field.initialValue !== undefined ?
                set(values, field.name as string | string[], field.initialValue)
            :   values,
        {},
    );

    const copyToClipboard = async (id) => {
        try {
            await navigator.clipboard.writeText(id);
            globalMessage.success('Copied to clipboard!');
        } catch {
            globalMessage.error('Failed to copy to clipboard');
        }
    };

    const hasAnySharedSettings = !!(branchConfig || tagConfig || featureAffiliationConfig);

    if (!hidden && isSettingFieldsError) {
        return <Alert showIcon type="error" message="Failed to load step settings" />;
    }

    if (!hidden && !settingFields) {
        return <Spin />;
    }

    return (
        <>
            <ExtendedForm
                autoComplete="off"
                hidden={hidden}
                extendedFormObject={stepConfigGeneralSetting}
                layout="vertical"
                preserve={false}
                initialValues={{ name: stepConfig.name }}
                disabled={isDisabled}
            >
                <Form.Item
                    label="Step name"
                    name="name"
                    rules={[{ required: true, message: 'Please enter a step name' }]}
                >
                    <Input />
                </Form.Item>
                <Space>
                    <Text type="secondary">Step Config ID: </Text>
                    <Text code>{stepConfig.id}</Text>
                    <Button
                        onClick={() => copyToClipboard(stepConfig.id)}
                        icon={<CopyOutlined style={{ color: runwayColors.primary }} />}
                        type="link"
                    />
                </Space>

                {hasAnySharedSettings && (
                    <>
                        <Divider />
                        <Text
                            style={{
                                ...titleStyle,
                                fontWeight: 400,
                                fontSize: 14,
                                marginBottom: 16,
                                display: 'block',
                            }}
                        >
                            Shared settings
                        </Text>

                        <Flex vertical gap={16}>
                            {branchConfig && <BranchConfigPreview branchConfig={branchConfig} />}
                            {tagConfig && <TagConfigPreview tagConfig={tagConfig} />}
                            {featureAffiliationConfig && (
                                <FeatureAffiliationConfigPreview featureAffiliationConfig={featureAffiliationConfig} />
                            )}
                        </Flex>
                    </>
                )}
            </ExtendedForm>
            <ExtendedForm
                hidden={hidden || stepConfigSettingsFields.length === 0}
                extendedFormObject={extendedFormObjectForArchetypeSpecificSettings}
                layout="vertical"
                autoComplete="off"
                preserve={false}
                initialValues={stepConfigSettingsInitialValues}
                disabled={isDisabled}
            >
                <Divider />
                <Text
                    style={{
                        ...titleStyle,
                        fontWeight: 400,
                        fontSize: 14,
                        marginBottom: 16,
                        display: 'block',
                    }}
                >
                    Step settings
                </Text>

                {renderFormFields(stepConfigSettingsFields)}
            </ExtendedForm>
        </>
    );
};

interface IntegrationsFormValues {
    integrationInstallationIdByCapability: Record<string, string | string[]>;
    [settingKey: string]: unknown;
}

const Integrations: React.FC<{
    orgId: string;
    appId: string;
    workflowId: string;
    stepConfig: StepConfig;
    addIntegrationIsLoading: Partial<Record<IntegrationType, boolean>>;
    addIntegrationSucceeded: Partial<Record<IntegrationType, boolean>>;
    hidden: boolean;
    isDisabled?: boolean;
    extendedFormObject: ExtendedFormObject<IntegrationsFormValues>;
}> = ({
    stepConfig,
    appId,
    orgId,
    workflowId,
    addIntegrationIsLoading,
    addIntegrationSucceeded,
    hidden,
    isDisabled,
    extendedFormObject,
}) => {
    const dispatch = useDispatch();

    const allAppIntegrations = useSelector((state: RootState) => selectAppIntegrations(state, appId));

    const allAppIntegrationsByCapability = AllIntegrationCapabilities.reduce(
        (acc, capability) => {
            return {
                ...acc,
                [capability]: allAppIntegrations.filter((integration) => integration.capabilities.includes(capability)),
            };
        },
        {} as Record<IntegrationCapability, Integration[]>,
    );

    const stepConfigConnectedIntegrations = useSelector((state: RootState) =>
        selectConnectedIntegrationsForStepConfig(state, appId, stepConfig.id),
    );

    const stepConfigConnectedIntegrationsByCapability = AllIntegrationCapabilities.reduce(
        (acc, capability) => {
            return {
                ...acc,
                [capability]: stepConfigConnectedIntegrations.filter((integration) =>
                    integration.capabilities.includes(capability),
                ),
            };
        },
        {} as Record<IntegrationCapability, Integration[]>,
    );

    const isRunwayOrg = useSelector((state: RootState) => selectIsRunwayOrg(state));

    const addIntegration = (integrationType: IntegrationType, data: CreateIntegrationRequestDataType) => {
        const request: CreateIntegrationRequest = {
            orgId,
            appId,
            integrationId: integrationType,
            data,
        };

        const authType = authTypeForIntegrationType(integrationType);

        if (authType) {
            dispatch(tryCreateIntegrationRequest(authType, request));
        } else {
            globalMessage.error('Unrecognized auth type for integration type');
        }
    };

    const initialValues: IntegrationsFormValues = {
        integrationInstallationIdByCapability: {},
    };

    const stepSupportsMultipleInstallations = (capability: IntegrationCapability): boolean =>
        stepConfig.archetype === ReleaseStepType.Rollout && capabilitySupportsMultipleInstallationsPerStep(capability);

    for (const [capability, integrations] of Object.entries<Integration[]>(
        stepConfigConnectedIntegrationsByCapability,
    )) {
        if (stepSupportsMultipleInstallations(capability as IntegrationCapability)) {
            initialValues.integrationInstallationIdByCapability[capability] = integrations.map(
                (integration) => integration.installationId,
            );
        } else {
            integrations.forEach((integration) => {
                initialValues.integrationInstallationIdByCapability[capability] = integration.installationId;
            });
        }
    }

    const { settingFields } = useStepConfigSettingFields({ orgId, workflowId, stepConfigId: stepConfig.id });
    const integrationSettingFields = (settingFields ?? []).filter(
        (field) => field.group === INTEGRATIONS_SETTINGS_GROUP,
    );
    const getSettingFieldsForInstallation = (capability: IntegrationCapability, installationId: string | undefined) =>
        installationId ?
            integrationSettingFields.filter(
                (field) =>
                    field.optionsBy?.field === `integrationInstallationIdByCapability.${capability}` &&
                    field.optionsBy.options?.[installationId] !== undefined,
            )
        :   [];

    const integrationInstallationIdByCapability = Form.useWatch(
        ['integrationInstallationIdByCapability'],
        extendedFormObject.form,
    );

    const renderIntegrationSection = (title: string, capabilities: IntegrationCapability[], isRequired = false) => {
        if (!capabilities || capabilities.length === 0) return null;

        return (
            <Form.Item shouldUpdate noStyle>
                {() => {
                    return (
                        <>
                            <Text
                                style={{
                                    ...titleStyle,
                                    fontWeight: 400,
                                    fontSize: 14,
                                    marginBottom: 16,
                                    display: 'block',
                                }}
                            >
                                {title}
                            </Text>

                            {capabilities.map((capability) => {
                                const integrations = allAppIntegrationsByCapability[capability];
                                const options = integrationSelectOptions(integrations);
                                const allSupportedIntegrations = allSupportedIntegrationTypesForCapability(
                                    capability,
                                    isRunwayOrg,
                                    orgId,
                                    true,
                                );

                                const connectedIntegrationTypes = integrations.map((integration) => integration.id);

                                const supportsMultiple = stepSupportsMultipleInstallations(capability);

                                const currentSelection = integrationInstallationIdByCapability?.[capability];
                                const currentIntegrationInstallationId =
                                    supportsMultiple ? undefined : (currentSelection as string | undefined);

                                return (
                                    <>
                                        <Form.Item
                                            name={['integrationInstallationIdByCapability', capability]}
                                            label={displayNameForCapability(capability)}
                                            required={isRequired}
                                        >
                                            <Select
                                                mode={supportsMultiple ? 'multiple' : undefined}
                                                disabled={isDisabled}
                                                options={options}
                                                placeholder="Please select"
                                                allowClear={!isRequired}
                                                optionFilterProp="title"
                                            />
                                        </Form.Item>

                                        {renderFormFields(
                                            formFieldsFromSettingFields(
                                                getSettingFieldsForInstallation(
                                                    capability,
                                                    currentIntegrationInstallationId,
                                                ),
                                            ),
                                        )}

                                        <Collapse defaultActiveKey={['1']} ghost style={{ marginBottom: 24 }}>
                                            <Collapse.Panel
                                                header={`Connect ${integrations.length > 0 ? 'another' : 'a'} ${displayNameForCapability(capability)} integration`}
                                                key={capability}
                                            >
                                                <PlanRestrictedFeatureWrapper integrationCapability={capability}>
                                                    {(isDisabled) => (
                                                        <IntegrationsForCapabilityComponent
                                                            integrationCapability={capability}
                                                            allSupportedIntegrationsForCapability={
                                                                allSupportedIntegrations
                                                            }
                                                            connectedIntegrationsForCapability={
                                                                connectedIntegrationTypes
                                                            }
                                                            isDisabled={isDisabled}
                                                            orgId={orgId}
                                                            appId={appId}
                                                            shouldUseSmallGrid
                                                            integrationIsLoading={addIntegrationIsLoading}
                                                            addIntegrationSuccess={addIntegrationSucceeded}
                                                            addIntegration={addIntegration}
                                                        />
                                                    )}
                                                </PlanRestrictedFeatureWrapper>
                                            </Collapse.Panel>
                                        </Collapse>
                                    </>
                                );
                            })}
                        </>
                    );
                }}
            </Form.Item>
        );
    };

    return (
        <ExtendedForm
            hidden={hidden}
            extendedFormObject={extendedFormObject}
            layout="vertical"
            preserve={false}
            initialValues={initialValues}
            disabled={isDisabled}
        >
            {renderIntegrationSection('Required integrations', stepConfig.requiredCapabilities, true)}

            {stepConfig.optionalCapabilities && stepConfig.optionalCapabilities.length > 0 && (
                <>
                    {stepConfig.requiredCapabilities?.length > 0 && <Divider />}
                    {renderIntegrationSection('Optional integrations', stepConfig.optionalCapabilities)}
                </>
            )}
        </ExtendedForm>
    );
};
